/**
 * SoundManager.ts — Procedural Web Audio API sound effect manager.
 *
 * Generates all animal barks/whines, critter chatters/meows/yips, and gameplay audio
 * procedurally using oscillators, noise buffers, and envelope filters.
 * Zero external mp3/wav files required, matching the project's Play-Doh asset philosophy.
 */

import {
  Emitter,
  GameEvents,
  DogState,
  Objective,
  type HazardType,
  type LossReason,
} from '../types';
import { mutedSetting, setMutedSetting } from '../ui/settings';

/**
 * The anatomy of a bark.
 *
 * The first version of this was a sawtooth swept from 460 Hz to 160 Hz with a
 * hiss over it, straight to the speakers, and it sounded like a duck being
 * stood on. Three things were missing, and all three are what the ear actually
 * listens for:
 *
 * 1. **A vowel.** A bark is "wo–uf": a mouth opening and closing around a
 *    voice. That's a pair of resonances (formants) sliding *down* across the
 *    sound, and it is the single biggest difference between an animal and a
 *    buzzer. FORMANTS below is that mouth.
 * 2. **A contour.** The larynx jumps above its resting pitch on the release
 *    and falls through the rest of the bark. A flat or slowly-swept tone reads
 *    as a horn.
 * 3. **A shape in time.** Nearly all attack, a short body, then gone. Barks
 *    are *short* — ~0.2s. The old one rang on for as long as a car horn.
 *
 * A ceiling over the lot (TOP) is the fourth thing: a raw sawtooth's upper
 * harmonics are a wasp, and no real animal has them.
 *
 * The same shape makes the fox's yip, which is the same instrument played
 * smaller and higher — see `playFoxYip`.
 */
const BARK = {
  /** How long one bark lasts, seconds. */
  LENGTH: 0.2,
  /** Time to full volume. A bark is a release, not a swell. */
  ATTACK: 0.008,
  /** How long the body holds at full before the fall. */
  BODY: 0.06,
  /** Resting larynx pitch (Hz), and what a full-strength lunge adds to it. */
  F0: 235,
  F0_LUNGE: 120,
  /** Pitch at the moment of release, as a multiple of F0 — the "r" of "ruff". */
  ONSET: 1.5,
  /** Pitch at the end, as a multiple of F0 — the falling "-uff". */
  FALL: 0.55,
  /** Peak output gain, and how much of it a limp bark keeps. */
  PEAK: 0.62,
  FLOOR: 0.45,
  /**
   * How much flatter the second voice falls than the first.
   *
   * The two start a few cents apart and drift further apart as the bark falls,
   * which beats against itself — the rasp in a real bark, and cheaper than
   * modulating anything.
   */
  RASP: 0.93,
  /**
   * The mouth: centre frequency at the release, where it has slid to by the
   * end, how sharp the resonance is, and how much of it is in the mix.
   */
  FORMANTS: [
    { from: 660, to: 430, q: 7, gain: 1 },
    { from: 1580, to: 1000, q: 9, gain: 0.85 },
    { from: 2700, to: 2150, q: 6, gain: 0.3 },
  ],
  /** How far a full lunge opens the mouth — wider mouth, higher formants. */
  OPEN: 0.2,
  /** The breath under the release: a short band of noise, not a hiss. */
  NOISE_LENGTH: 0.05,
  NOISE_HZ: 1500,
  NOISE_GAIN: 0.35,
  /** Ceiling on the buzz. */
  TOP: 4200,
} as const;

export class SoundManager {
  private ctx: AudioContext | null = null;
  private muted = false;

  /**
   * Called after the mute setting changes, whoever changed it — the HUD button,
   * a keypress, or code. Cloud save hangs off this so it doesn't have to guess
   * at every route to a toggle. Set by `main.ts`, like `Game.onRestart`.
   */
  onMuteChange?: () => void;
  private unlocked = false;
  private subs: Array<[string, (...args: unknown[]) => void]> = [];
  // -Infinity, not 0: these are throttles, and seeding them at zero swallows
  // the *first* bark or whine of a session whenever the page has been open for
  // less than the interval.
  private lastWhineTime = -Infinity;
  private lastBarkTime = -Infinity;

  constructor(private events?: Emitter) {
    // Read the persisted setting. ui/settings.ts owns the key and absorbs the
    // restricted-environment failures, so there is nothing to guard here.
    this.muted = mutedSetting();

    this.initAudioContext();
    if (this.events) {
      this.bindEvents(this.events);
    }
  }

  private initAudioContext(): void {
    if (typeof window === 'undefined') return;
    const AudioCtxClass =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioCtxClass) {
      try {
        this.ctx = new AudioCtxClass();
      } catch {
        this.ctx = null;
      }
    }
  }

  /**
   * Unlock AudioContext on the first user interaction to comply with browser autoplay policies.
   */
  unlock(): void {
    if (this.unlocked || !this.ctx) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {
        // Ignore resume failures.
      });
    }
    this.unlocked = true;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setMuted(mute: boolean): boolean {
    this.muted = mute;
    setMutedSetting(mute);
    this.onMuteChange?.();
    return this.muted;
  }

  toggleMute(): boolean {
    return this.setMuted(!this.muted);
  }

  private bindEvents(events: Emitter): void {
    // Barking is no longer a state change — the dog barks the whole time it is
    // lunging, on its own timer (SOUND_DOG_BARK below), so the only thing left
    // for a state to announce is the squat starting.
    // …and the whine that comes before it. A dog that has started sniffing for
    // somewhere to go is the player's cue to find a park, and a cue that only
    // exists on screen is one anybody watching the road will miss.
    const onDogState = (state: unknown): void => {
      if ((state as DogState) === DogState.POOPING) this.playDogGrunt();
      else if ((state as DogState) === DogState.SNIFFING) this.playDogWhine();
    };

    const onObjective = (obj: unknown): void => {
      const o = obj as Objective;
      if (o === Objective.CARRYING_BAG) {
        this.playScoop();
      } else if (o === Objective.GO_HOME) {
        this.playBin();
      }
    };

    // A whine near the top of the frenzy meter: the last audible warning that
    // the dog is about to get away, over the top of its own barking.
    const onFrenzy = (ratio: unknown): void => {
      const r = ratio as number;
      const now = performance.now();
      if (r > 0.85 && now - this.lastWhineTime > 1500) {
        this.lastWhineTime = now;
        this.playDogWhine();
      }
    };

    const onBark = (intensity: unknown): void => this.playDogBark(intensity as number);
    const onHorn = (): void => this.playCarHorn();

    const onCritterSpotted = (type: unknown): void => {
      const t = type as HazardType;
      if (t === 'cat') this.playCatMeow();
      else if (t === 'squirrel') this.playSquirrelChatter();
      else if (t === 'fox') this.playFoxYip();
      else if (t === 'pigeon') this.playPigeonCoo();
    };

    const onCritterStartle = (type: unknown): void => {
      const t = type as HazardType;
      if (t === 'cat') this.playCatHiss();
      else if (t === 'squirrel') this.playSquirrelChatter();
      else if (t === 'fox') this.playFoxYip();
      // For a pigeon, "startled" only ever means a bark put it in the air, so
      // the reaction sound is the take-off itself.
      else if (t === 'pigeon') this.playPigeonFlush();
    };

    const onWon = (): void => this.playWin();
    // The car horn is the loss sound for a car; a dog that has broken loose
    // gets the whine instead, because a horn there names the wrong culprit.
    const onLost = (reason: unknown): void =>
      (reason as LossReason) === 'frenzy' ? this.playDogWhine() : this.playLose();

    const bindings: Array<[string, (...a: never[]) => void]> = [
      [GameEvents.DOG_STATE, onDogState],
      [GameEvents.OBJECTIVE, onObjective],
      [GameEvents.FRENZY, onFrenzy],
      [GameEvents.SOUND_DOG_BARK, onBark],
      [GameEvents.SOUND_CAR_HORN, onHorn],
      [GameEvents.WON, onWon],
      [GameEvents.LOST, onLost],
      [GameEvents.SOUND_CRITTER_SPOTTED, onCritterSpotted],
      [GameEvents.SOUND_HAZARD_STARTLE, onCritterStartle],
    ];

    for (const [event, fn] of bindings) {
      events.on(event, fn);
      this.subs.push([event, fn as (...a: unknown[]) => void]);
    }
  }

  // ---- Procedural Sound Generators ----------------------------------------------

  /**
   * The dog's bark.
   *
   * `intensity` (0..1) is how hard it is lunging — it opens the mouth wider,
   * pitches the voice up and leans on the volume, so a dog straining at a cat
   * under its nose sounds like a different animal from one grumbling at a
   * squirrel across the road. The floor keeps a distant bark audible rather
   * than polite.
   */
  playDogBark(intensity = 1): void {
    if (this.muted || !this.ctx) return;
    const now = performance.now();
    // Just under DOG.BARK_MIN_MS, so a full-tilt bark rate is never swallowed.
    if (now - this.lastBarkTime < 200) return; // Debounce rapid barks
    this.lastBarkTime = now;

    this.unlock();
    this.bark(Math.min(1, Math.max(0, intensity)));
  }

  /**
   * One bark, built the way an animal makes one — see `BARK` above for what the
   * pieces are and why a bark needs all of them.
   *
   * The graph is a voice, a mouth and a breath:
   *
   *     saw + square ─┐                          ┌─ formant 1 ─┐
   *      (the larynx) ├─ voice ──────────────────┼─ formant 2 ─┼─┐
   *                   ┘                          └─ formant 3 ─┘ │
   *     noise burst ─── band ─────────────────────────────────── ├─ lowpass ─ envelope ─ out
   *      (the breath)                                            ┘
   *
   * `pitch` scales the whole animal: 1 is the dog on the end of the lead, and
   * anything above it is something smaller with a shorter throat.
   */
  private bark(intensity: number, pitch = 1, length: number = BARK.LENGTH): void {
    const ctx = this.ctx;
    if (!ctx) return;

    const t = ctx.currentTime;
    const end = t + length;
    const f0 = (BARK.F0 + BARK.F0_LUNGE * intensity) * pitch;
    // A wider mouth on a harder lunge: the formants ride up with it.
    const open = (1 - BARK.OPEN / 2 + BARK.OPEN * intensity) * pitch;

    // The envelope, and the ceiling that stops a sawtooth being a wasp.
    const out = ctx.createGain();
    const peak = BARK.PEAK * (BARK.FLOOR + (1 - BARK.FLOOR) * intensity);
    out.gain.setValueAtTime(0.0001, t);
    out.gain.exponentialRampToValueAtTime(peak, t + BARK.ATTACK);
    out.gain.setValueAtTime(peak, t + BARK.ATTACK + BARK.BODY);
    out.gain.exponentialRampToValueAtTime(0.0001, end);
    out.connect(ctx.destination);

    const lid = ctx.createBiquadFilter();
    lid.type = 'lowpass';
    lid.frequency.setValueAtTime(BARK.TOP * pitch, t);
    lid.connect(out);

    // The voice: two sources a hair apart, because one clean oscillator is a
    // synthesiser and a larynx is never quite in tune with itself. They also
    // fall at slightly *different* rates, so the interval between them drifts
    // over the bark — that drift is the rasp, and a bark with none of it is a
    // choirboy.
    const voice = ctx.createGain();
    voice.gain.setValueAtTime(1, t);
    for (const [type, detune, level, fall] of [
      ['sawtooth', 0, 1, 1],
      ['square', -9, 0.35, BARK.RASP],
    ] as const) {
      const osc = ctx.createOscillator();
      const mix = ctx.createGain();
      osc.type = type;
      osc.detune.setValueAtTime(detune, t);
      // Up on the release, then falling all the way out — the "r-uff".
      osc.frequency.setValueAtTime(f0 * BARK.ONSET, t);
      osc.frequency.exponentialRampToValueAtTime(f0, t + BARK.ATTACK * 2);
      osc.frequency.exponentialRampToValueAtTime(f0 * BARK.FALL * fall, end);
      mix.gain.setValueAtTime(level, t);
      osc.connect(mix);
      mix.connect(voice);
      osc.start(t);
      osc.stop(end);
    }

    // The mouth. Parallel rather than in series: three resonances *added*, each
    // sliding down over the bark, is a vowel closing. In series they'd multiply
    // into one narrow whistle.
    for (const f of BARK.FORMANTS) {
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.Q.setValueAtTime(f.q, t);
      band.frequency.setValueAtTime(f.from * open, t);
      band.frequency.exponentialRampToValueAtTime(f.to * open, end);

      const level = ctx.createGain();
      level.gain.setValueAtTime(f.gain, t);

      voice.connect(band);
      band.connect(level);
      level.connect(lid);
    }

    // The breath: a band of noise under the release. Banded, not high-passed —
    // a high-pass leaves the top open and turns the breath into a cymbal.
    const breath = this.noiseSource(BARK.NOISE_LENGTH);
    if (breath) {
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.Q.setValueAtTime(1.1, t);
      band.frequency.setValueAtTime(BARK.NOISE_HZ * pitch, t);

      const level = ctx.createGain();
      level.gain.setValueAtTime(BARK.NOISE_GAIN, t);
      level.gain.exponentialRampToValueAtTime(0.0001, t + BARK.NOISE_LENGTH);

      breath.connect(band);
      band.connect(level);
      level.connect(lid);
      breath.start(t);
      breath.stop(t + BARK.NOISE_LENGTH);
    }
  }

  /**
   * Car horn: a short two-tone parp from a vehicle bearing down on the pair.
   *
   * Deliberately a near relative of `playLose` — same interval, a third of the
   * length and quieter. It's the sound the game over makes, arriving early
   * enough to do something about, which is exactly what it means.
   */
  playCarHorn(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    const t = this.ctx.currentTime;

    for (const freq of [240, 300]) {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, t);

      gain.gain.setValueAtTime(0.12, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16);

      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start(t);
      osc.stop(t + 0.16);
    }
  }

  /** Strained whine — a dog at the top of the frenzy meter. */
  playDogWhine(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    const t = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(900, t);
    osc.frequency.linearRampToValueAtTime(1200, t + 0.12);
    osc.frequency.linearRampToValueAtTime(850, t + 0.25);

    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.08, t + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);

    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(t);
    osc.stop(t + 0.25);
  }

  /** Dog poop grunt. */
  playDogGrunt(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    const t = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(130, t);
    osc.frequency.exponentialRampToValueAtTime(70, t + 0.18);

    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);

    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(t);
    osc.stop(t + 0.18);
  }

  /** Cat meow: smooth frequency sweep (750 Hz → 980 Hz → 620 Hz). */
  playCatMeow(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    const t = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(750, t);
    osc.frequency.linearRampToValueAtTime(980, t + 0.16);
    osc.frequency.linearRampToValueAtTime(620, t + 0.35);

    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.15, t + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(t);
    osc.stop(t + 0.35);
  }

  /** Cat hiss: high-pass filtered white noise. */
  playCatHiss(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    this.playNoise(0.2, 0.12, 3500);
  }

  /** Squirrel chatter: rapid micro-chirps. */
  playSquirrelChatter(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();

    const chirps = 4;
    for (let i = 0; i < chirps; i++) {
      const delay = i * 0.05;
      const freq = 2300 + (i % 2 === 0 ? 300 : -200);
      this.playChirp(freq, 0.035, delay, 0.1);
    }
  }

  /**
   * Fox yip — the same instrument as the dog's bark, played small.
   *
   * A fox's yip *is* a bark: shorter throat, higher voice, and over almost
   * before it starts. Built from `bark` rather than its own oscillator so the
   * two stay recognisably the same kind of animal noise, which is the point —
   * a fox yipping across the street should make the player look for a dog.
   */
  playFoxYip(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    this.bark(0.8, 1.9, 0.13);
  }

  /**
   * Pigeon coo — the low two-part "roo-coo" of a bird that hasn't noticed you.
   *
   * Deliberately the *lowest* critter voice in the game. Cat, fox and squirrel
   * all live between 600 Hz and 2 kHz, and a fourth animal in that band is one
   * more chirp nobody can pick out; a pigeon sits underneath all of them, which
   * is both true to the bird and the only free space left in the mix.
   */
  playPigeonCoo(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    const t = this.ctx.currentTime;

    // Two syllables, the second lower and longer — the shape of a real coo.
    const syllables: [number, number, number, number][] = [
      // [start Hz, end Hz, delay, length]
      [330, 300, 0, 0.16],
      [300, 250, 0.2, 0.3],
    ];
    for (const [from, to, delay, length] of syllables) {
      const at = t + delay;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      // A throaty vowel rather than a whistle: the low-passed triangle is what
      // keeps it from sounding like a tuning fork.
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(from, at);
      osc.frequency.linearRampToValueAtTime(to, at + length);

      const throat = this.ctx.createBiquadFilter();
      throat.type = 'lowpass';
      throat.frequency.setValueAtTime(900, at);

      gain.gain.setValueAtTime(0.001, at);
      gain.gain.linearRampToValueAtTime(0.11, at + 0.06);
      gain.gain.exponentialRampToValueAtTime(0.001, at + length);

      osc.connect(throat);
      throat.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start(at);
      osc.stop(at + length);
    }
  }

  /**
   * A flock going up: the clatter of wings, and one alarmed coo under it.
   *
   * The clatter is four short bursts of band-passed noise rather than one, and
   * that is the whole sound — wingbeats are *discrete*, and a single smooth
   * whoosh reads as a car going past rather than as birds.
   */
  playPigeonFlush(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    for (let i = 0; i < 4; i++) {
      // Slowing slightly as they climb away.
      this.playChirp(180 + i * 30, 0.05, i * 0.075, 0.05, 'square');
      this.playNoise(0.07, 0.09 - i * 0.015, 1400, i * 0.075);
    }
    this.playPigeonCoo();
  }

  /** Scoop chime: ascending 4-note bell synth. */
  playScoop(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();

    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      this.playChirp(freq, 0.15, i * 0.07, 0.15, 'sine');
    });
  }

  /** Bin drop: low impact thud + noise pop. */
  playBin(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    const t = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.2);

    gain.gain.setValueAtTime(0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);

    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(t);
    osc.stop(t + 0.2);

    this.playNoise(0.12, 0.15, 800);
  }

  /** Victory fanfare: cheerful ascending arpeggio. */
  playWin(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();

    const notes = [261.63, 329.63, 392.0, 523.25, 659.25]; // C4, E4, G4, C5, E5
    notes.forEach((freq, i) => {
      this.playChirp(freq, 0.25, i * 0.09, 0.18, 'triangle');
    });
  }

  /** Loss sound: dual-tone car horn. */
  playLose(): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    const t = this.ctx.currentTime;

    [180, 225].forEach((freq) => {
      if (!this.ctx) return;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, t);

      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);

      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
    });
  }

  // ---- Helper Audio Utilities ---------------------------------------------------

  private playChirp(
    freq: number,
    duration: number,
    delaySec: number,
    volume: number,
    type: OscillatorType = 'sine',
  ): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime + delaySec;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);

    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(volume, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(t);
    osc.stop(t + duration);
  }

  /**
   * `duration` seconds of white noise, ready to be filtered into something.
   *
   * Split out from `playNoise` because the bark needs the noise *inside* its
   * own graph rather than sent straight to the speakers — the breath belongs
   * under the bark's envelope, not beside it.
   */
  private noiseSource(duration: number): AudioBufferSourceNode | null {
    if (!this.ctx) return null;
    const bufferSize = Math.max(1, Math.floor(this.ctx.sampleRate * duration));
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    return source;
  }

  private playNoise(
    duration: number,
    volume: number,
    highPassCutoff = 1000,
    delaySec = 0,
  ): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime + delaySec;
    const whiteNoise = this.noiseSource(duration);
    if (!whiteNoise) return;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(highPassCutoff, t);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    whiteNoise.connect(filter);
    filter.connect(gain);
    gain.connect(this.ctx.destination);
    whiteNoise.start(t);
    whiteNoise.stop(t + duration);
  }

  dispose(): void {
    if (this.events) {
      for (const [evt, fn] of this.subs) {
        this.events.off(evt, fn);
      }
      this.subs = [];
    }
    if (this.ctx) {
      this.ctx.close().catch(() => {
        // Ignore close errors
      });
      this.ctx = null;
    }
  }
}
